import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/rest";
import type { Market } from "../api/types";
import { BookLadder } from "../components/BookLadder";
import { CCFeedPanel } from "../components/CCFeedPanel";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { MarketOverview } from "../components/MarketOverview";
import { OrderForm } from "../components/OrderForm";
import { AIPanel } from "../components/AIPanel";
import { OrdersPanel } from "../components/OrdersPanel";
import { Empty, Panel } from "../components/Panel";
import { ParticipantsPanel } from "../components/ParticipantsPanel";
import { PredictOrderForm } from "../components/PredictOrderForm";
import { PredictPanel } from "../components/PredictPanel";
import { QuickActions } from "../components/QuickActions";
import { AccountPicker } from "../components/AccountPicker";
import { SimSwitch } from "../components/SimSwitch";
import { WindowExpired } from "../components/SeriesNav";
import { TapePanel } from "../components/TapePanel";
import { TimelinePanel } from "../components/TimelinePanel";
import { VenueSwitch } from "../components/VenueSwitch";
import { useMarketSubscription } from "../hooks/useMarketSubscription";
import { useMarketPnl } from "../hooks/usePnl";
import { usePFStatus } from "../hooks/usePredict";
import { pairedSlug } from "../lib/predict";
import { parseSeriesSlug } from "../lib/series";
import { trackingNotice, trackingPollMs } from "../lib/tracking";
import { useBooks } from "../store/useBooks";
import { useConn } from "../store/useConn";
import { useSettings } from "../store/useSettings";

export function MarketPage() {
  const { conditionID } = useParams();
  const queryClient = useQueryClient();

  const market = useQuery({
    queryKey: ["market", conditionID],
    queryFn: () => api.market(conditionID ?? "").then((r) => r.market),
    enabled: Boolean(conditionID),
    // Рынок почти всегда уже лежит в кэше списка, так что стаканы начинают
    // грузиться сразу, не дожидаясь отдельного запроса.
    initialData: () =>
      queryClient
        .getQueryData<{ markets: Market[] }>(["markets"])
        ?.markets.find((m) => m.condition_id === conditionID),
    // Посев — со временем, когда список ЧИТАЛСЯ, а не «сейчас»: без этого
    // строка из кэша получаса считалась бы свежей ещё staleTime, и флаг
    // подписки жил бы на странице до минуты после смены. По нему решается,
    // показывать ли тревогу «не отслеживается».
    initialDataUpdatedAt: () => queryClient.getQueryState(["markets"])?.dataUpdatedAt,
    staleTime: 30_000,
    // Флаг подписки меняется за секунды до открытия окна, и только вокруг
    // этого момента рынок перечитывается часто. Логика в lib/tracking.ts.
    refetchInterval: (query) => {
      const m = query.state.data;
      return m ? trackingPollMs(m.slug, m.tracked, Date.now()) : false;
    },
  });

  // Пара окна на predict.fun (ADR-0040): slug у площадок общий. Форма
  // переключается на площадку, только если пара у окна есть, — иначе
  // сохранённый выбор predict.fun молча оставил бы окно без формы.
  // Пара считается, только когда сервис predictfun отвечает статусом: при
  // выключенной пробе (PF_ENABLED=false, умолчание) ни панели, ни
  // переключателя на странице нет, а не висят с ошибкой на каждом окне.
  const pfPaired = pairedSlug(market.data?.slug);
  const pfStatus = usePFStatus(pfPaired !== null);
  const pfSlug = pfStatus.data ? pfPaired : null;
  const venue = useSettings((s) => s.settings.venue);
  const pfActive = venue === "predict" && pfSlug !== null;

  /**
   * Справочник рынков — ради навигации по окнам серии.
   *
   * Свой запрос, а не подглядывание в кэш дашборда: при заходе по прямой ссылке
   * кэш пуст, и соседних окон было бы не найти именно в том случае, ради
   * которого навигация и заведена — оператор пришёл по ссылке на рынок, который
   * уже закончился.
   *
   * Ключ тот же, что у дашборда: TanStack дедуплицирует, лишнего запроса не
   * появляется.
   */
  const markets = useQuery({
    queryKey: ["markets"],
    queryFn: api.markets,
    refetchInterval: 30_000,
  });
  const marketList = useMemo(() => markets.data?.markets ?? [], [markets.data]);

  // Момент, когда флаг подписки ЧИТАЛСЯ: им «трейдер не подписан на живое
  // окно» отличается от «мы смотрим на кэш, снятый до открытия». Чтение
  // dataUpdatedAt заодно подписывает страницу на каждый опрос флага выше —
  // пока флаг спорный, пометки пересчитываются раз в несколько секунд.
  const marketFetchedAt = market.dataUpdatedAt;
  // Пометка для формы ордера считается здесь без тика часов: форму нельзя
  // перерисовывать каждую секунду (см. hooks/useNow.ts), а опроса флага
  // хватает, чтобы она не отстала больше чем на несколько секунд. Тикающая
  // версия той же пометки — в шапке (TrackingBadge).
  const trackingNote = market.data
    ? trackingNotice(market.data.slug, market.data.tracked, new Date(), marketFetchedAt)?.text
    : undefined;

  // Порядок здесь значим: подписка уходит РАНЬШЕ запроса снапшотов, иначе
  // дельты между чтением ключа и подпиской теряются безвозвратно, а эта дыра
  // сама уже не закроется.
  useMarketSubscription(conditionID);

  /**
   * Счёт окна. Спрашивается ЗДЕСЬ, а не только в панели, ради одного поля:
   * тумблер симуляции обязан знать, поднята ли подсистема проб на трейдере, и
   * гаснуть с объяснением вместо того, чтобы принимать нажатия, которые всё
   * равно отвергнутся. Второго запроса это не стоит — ключ у TanStack общий.
   */
  const pnl = useMarketPnl(conditionID);
  const paperEnabled = pnl.data?.paper_enabled ?? true;

  const generation = useConn((s) => s.generation);
  const connection = useConn((s) => s.connection);

  /**
   * Исходы В ПОРЯДКЕ outcome_index, а не как пришли из справочника.
   *
   * Порядок стал значимым, когда панель быстрых действий получила ряд на исход,
   * а клавиши Q/W и A/S — привязку к ряду. Возьми массив как есть — и смена
   * порядка в справочнике молча переназначила бы клавишу на другой исход,
   * оставив её при этом рабочей. Сортировка здесь одна на страницу: и лесенки,
   * и панель получают уже упорядоченное.
   */
  const tokens = useMemo(
    () => [...(market.data?.tokens ?? [])].sort((a, b) => a.outcome_index - b.outcome_index),
    [market.data?.tokens],
  );
  const assetKey = tokens.map((t) => t.asset_id).join(",");

  /**
   * Исход, на который нацелена ФОРМА ордера и клик по лесенке.
   *
   * Раньше это был «выбранный исход экрана»: панель быстрых действий работала
   * по нему же, и держать два независимых «текущих токена» было нельзя — на
   * бинарном рынке покупка NO по 0.48 и есть продажа YES по 0.52, так что
   * рассогласование означало бы продать то, что собирался купить.
   *
   * С перестройкой панели опасность ушла сама: панель работает по ОБОИМ исходам
   * сразу и ничего не «выбирает». Осталась только форма, у которой прицел один,
   * и клик по лесенке, который его переставляет. Панель его НЕ трогает — иначе
   * нажатие Q переводило бы форму на другой исход под руками оператора, который
   * её как раз заполняет.
   */
  const [assetId, setAssetId] = useState<string>("");
  useEffect(() => {
    // Первый токен по умолчанию; сбрасывается при смене рынка и не теряется при
    // обновлении справочника.
    if (assetId && tokens.some((t) => t.asset_id === assetId)) return;
    setAssetId(tokens[0]?.asset_id ?? "");
  }, [assetKey, assetId, tokens]);

  /**
   * Клик по уровню лесенки. Самое ценное взаимодействие на экране: оно же
   * делает так, что проверка кратности шагу почти никогда не срабатывает.
   *
   * Несёт актив и НОМЕР клика. Актив — потому что без него клик по лесенке NO
   * подставлял цену NO в ордер на YES (см. BookLadder). Номер — потому что
   * эффект в форме следит за значением, и клик по ТОЙ ЖЕ цене второй раз иначе
   * не менял бы ничего, то есть выглядел бы как сломанная лесенка.
   */
  const [picked, setPicked] = useState<{ assetId: string; price: string; nonce: number } | null>(
    null,
  );
  const pickPrice = useCallback((asset: string, price: string) => {
    setAssetId(asset);
    setPicked((prev) => ({ assetId: asset, price, nonce: (prev?.nonce ?? 0) + 1 }));
  }, []);

  /**
   * Лесенки стаканов — СВЁРНУТЫ по умолчанию.
   *
   * Свёрнуты, а не удалены: клик по уровню остаётся единственным способом
   * поставить в форму цену, которая заведомо кратна шагу, и терять его нельзя.
   * Но два стакана занимают верхний экран целиком, а решение оператор принимает
   * по накоплению участников — вот оно и стоит на их месте, пока стаканы не
   * позвали кнопкой в шапке. Подписка на стаканы при этом ЖИВЁТ: по ней считает
   * панель быстрых действий, и гасить её значило бы обезоружить клавиши.
   *
   * Состояние держится страницей, а не хранилищем: переход по окнам серии его
   * сохраняет (страница не размонтируется), перезагрузка — сбрасывает к
   * умолчанию, и это правильное умолчание для нового сеанса.
   */
  const [books, setBooks] = useState(false);

  /**
   * Фактическая высота липкой полосы — в CSS-переменную `--pm-stack-top`.
   *
   * Нижние панели прокручиваются внутри себя, и потолок им задавался в `vh`. Это
   * не тот измеритель: `vh` меряет экран, а таблица живёт в ОСТАТКЕ экрана под
   * прилипшей полосой. При полосе в 640 пикселей `70vh` давал таблицу вдвое выше
   * её смотровой щели, то есть двойную прокрутку — сначала страницы, потом
   * внутри таблицы.
   *
   * Высота пишется ИМПЕРАТИВНО, а не через состояние: полоса меняет высоту при
   * каждом замечании и каждом ответе трейдера, а перерисовка этой страницы тянет
   * за собой весь экран. Переменная наследуется вниз по дереву, и панели читают
   * её из своих классов.
   */
  const rootRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const applyStackTop = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    root.style.setProperty("--pm-stack-top", `${stackRef.current?.offsetHeight ?? 0}px`);
  }, []);

  /**
   * Страховка на КАЖДЫЙ рендер страницы.
   *
   * В скрытой вкладке браузер приостанавливает ResizeObserver, и переменная
   * застывает на высоте, снятой до ухода. Оператор держит терминал в фоне
   * постоянно — вернувшись, он получил бы таблицы, посчитанные по вчерашней
   * полосе. Чтение offsetHeight здесь стоит одного пересчёта раскладки, а
   * страница рынка перерисовывается редко: справочник опрашивается раз в
   * полминуты, всё живое сидит в листьях.
   */
  useLayoutEffect(applyStackTop);

  useEffect(() => {
    const stack = stackRef.current;
    if (!stack) return;
    const observer = new ResizeObserver(applyStackTop);
    observer.observe(stack);
    return () => observer.disconnect();
    // Полоса появляется только вместе с рынком и токенами: до этого наблюдать
    // нечего, и эффект обязан пересобраться, когда узел наконец возник.
  }, [applyStackTop, market.data, tokens.length]);

  // Смена рынка ПО ССЫЛКЕ СЕРИИ не размонтирует страницу: маршрут тот же,
  // меняется только параметр. Цена из лесенки прошлого окна, оставшаяся в
  // форме, — это цена чужого стакана, и отправить по ней ордер тем легче, что
  // поле выглядит заполненным.
  useEffect(() => {
    setPicked(null);
  }, [conditionID]);

  useEffect(() => {
    if (!assetKey || !conditionID || connection !== "open") return;
    const ids = assetKey.split(",");
    const books = useBooks.getState();
    for (const id of ids) books.open(id, conditionID);
    return () => {
      for (const id of ids) books.close(id);
    };
    // generation в зависимостях — это и есть восстановление после разрыва: за
    // время обрыва канал потерял дельты, цепочка seq порвана по определению, и
    // снапшоты надо перечитать. Один и тот же код обслуживает и первое открытие
    // рынка, и каждое переподключение.
  }, [assetKey, conditionID, connection, generation]);

  if (!conditionID) {
    return (
      <Panel title="Рынок не выбран">
        <Empty>
          В адресе нет идентификатора рынка. <Link to="/">Вернуться к списку</Link>.
        </Empty>
      </Panel>
    );
  }

  if (market.isLoading && !market.data) {
    return (
      <Panel title="Рынок">
        <Empty>загрузка…</Empty>
      </Panel>
    );
  }

  if (market.error && !market.data) {
    return (
      <Panel title="Рынок">
        <p className="text-sm text-[var(--color-sell)]">{(market.error as Error).message}</p>
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          <Link to="/" className="underline">
            вернуться к списку рынков
          </Link>
        </p>
      </Panel>
    );
  }

  return (
    /* gap-2 вместо gap-4: карточек на экране много, и половина вертикального
       воздуха между ними — это половина лишней прокрутки к панели ордеров. */
    <div ref={rootRef} className="flex flex-col gap-2">
      {/*
        Полоса действий ВНЕ сетки и во всю ширину: индикатор боевого режима
        нельзя прокрутить с экрана в тот момент, когда нажатие клавиши тратит
        деньги, а к панели ордеров оператор скроллит постоянно. Приглашение уйти
        на идущее окно живёт здесь же и по той же причине.

        Форма ордера стоит ЗДЕСЬ ЖЕ, вторым столбцом, а не в правой колонке
        сетки. Это два способа отправить одну и ту же команду — быстрый и
        точный, — и держать их на разной высоте значило заставлять оператора
        прокручивать экран, чтобы перейти от одного к другому.

        Липкость — только с xl, и это вынужденно: липким может быть лишь то, что
        лежит прямо в колонке страницы (иначе прокрутка ограничена своим рядом), а
        ниже xl столбцы схлопываются в стопку выше экрана. Прилипшая полоса
        высотой в полтора экрана не показывает нижнюю половину себя вовсе —
        именно те кнопки, ради которых она и липкая.
      */}
      {market.data && tokens.length > 0 && (
        <div
          ref={stackRef}
          className="static z-10 -mx-6 flex flex-col gap-2 bg-[var(--color-bg)] px-6 py-0.5 xl:sticky xl:top-0"
        >
          <WindowExpired market={market.data} markets={marketList} />
          {/*
            Две колонки одной высоты. Слева — карточка рынка и панель быстрых
            действий, справа — селектор цели формы и сама форма. Карточка стоит НАД панелью, а не над
            всей страницей, потому что описывает ровно то, по чему панель бьёт:
            окно, шаг цены и минимальный размер читают в тот же момент, когда
            смотрят на кнопки.

            items-stretch (умолчание grid) плюс flex-1 у формы: форма тянется до
            низа панели, и правый край полосы не ступенчатый. Ширина второй
            колонки та же, что у правой колонки сетки ниже, — форма встаёт ровно
            над лентой сделок.
          */}
          <div className="grid gap-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,420px)]">
            <div className="flex min-w-0 flex-col gap-2">
              {/* Всё про идущее окно — одной карточкой и внутри липкой полосы:
                  окно, отсчёт, страйк, индекс, сверка и замок отвечают на один
                  вопрос («что сейчас с этим рынком») и читаются подряд. Разъехавшись
                  по экрану, они стоили бы прокрутки от кнопок к числам и обратно. */}
              <MarketOverview
                market={market.data}
                conditionID={conditionID}
                markets={marketList}
                fetchedAt={marketFetchedAt}
                books={books}
                onToggleBooks={() => setBooks((v) => !v)}
              />
              {/* Тумблер симуляции — в ЛИПКОЙ полосе и рядом с кнопками, которые
                  он переключает. Уехав ниже по странице, он оказался бы вне
                  глаз ровно в тот момент, когда нажатие тратит деньги, — та же
                  причина, по которой здесь же стоит индикатор боевого режима. */}
              <div className="flex items-center gap-2">
                {/* Выбора аккаунта здесь больше нет: у каждой строки быстрых
                    действий цель своя, а селектор уехал к форме ордера и
                    действует только на неё. */}
                <SimSwitch
                  disabled={!paperEnabled}
                  reason="пробные ордера выключены на трейдере (PAPER_ENABLED=false)"
                />
                <span className="min-w-0 truncate text-xs text-[var(--color-muted)]">
                  {paperEnabled
                    ? "пробный ордер считает налив по стакану и не тратит денег"
                    : "трейдер не принимает пробные ордера: PAPER_ENABLED=false"}
                </span>
              </div>
              {/* flex-1: панель добирает высоту до низа формы, и обе колонки
                  кончаются на одной линии — иначе левый край полосы обрывается
                  выше правого, а полоса липкая и этот обрыв виден постоянно. */}
              <QuickActions market={market.data} tokens={tokens} pfSlug={pfSlug} className="flex-1" />
            </div>
            {/* Селектор аккаунта — НАД формой и только для неё (замечание
                владельца 2026-10-07): быстрые действия бьют от своих строк, и
                общий выбор над ними читался бы как цель кнопок, которой он
                больше не является. flex-1 у формы — та же причина, что была
                у h-full: правый край полосы кончается на одной линии с левым. */}
            <div className="flex min-w-0 flex-col gap-2">
              {/* Площадка формы (ADR-0040): у predict.fun один кошелёк пробы, и
                  выбор аккаунта на его месте читался бы как цель, которой у
                  этой площадки нет. Обе формы СМОНТИРОВАНЫ, скрыта неактивная:
                  набранное в форме Polymarket не теряется на переключении. */}
              <div className="flex min-w-0 items-center gap-1.5">
                <VenueSwitch paired={pfSlug !== null} />
                {pfActive ? (
                  <span className="min-w-0 truncate text-xs text-[var(--color-muted)]">
                    один кошелёк пробы, без аккаунтов и когорт
                  </span>
                ) : (
                  <AccountPicker scope="form" />
                )}
              </div>
              <OrderForm
                market={market.data}
                tokens={tokens}
                assetId={assetId}
                onAssetChange={setAssetId}
                picked={picked}
                trackingNote={trackingNote}
                className={pfActive ? "hidden" : "flex-1"}
              />
              {pfSlug && (
                <ErrorBoundary fallback={predictFallback}>
                  <PredictOrderForm slug={pfSlug} className={pfActive ? "flex-1" : "hidden"} />
                </ErrorBoundary>
              )}
            </div>
          </div>
        </div>
      )}

      {!(market.data && tokens.length > 0) && (
        <MarketOverview
          market={market.data}
          conditionID={conditionID}
          markets={marketList}
          fetchedAt={marketFetchedAt}
          books={books}
          onToggleBooks={() => setBooks((v) => !v)}
        />
      )}

      {/* Ход накопления и «Мои ордера» — ОДНОЙ строкой над сеткой.
          График во всю оставшуюся ширину, панель — в те же 420 пикселей, что
          форма ордера над ней и лента сделок под ней: правый край страницы
          обязан быть прямым, иначе колонка выглядит собранной случайно.

          Панель уехала сюда снизу вместе с объединением: «что я отправил» и
          «что у меня в позиции» — один вопрос, и читают его до того, как
          смотреть на состав участников. Прежде к ней приходилось прокручивать
          мимо двух таблиц.

          Почему график по-прежнему НЕ в общей сетке. Ряд сетки берёт высоту по
          большей из панелей ряда, и в любой её ячейке график зависел бы от
          соседей — а под ним стоит content-driven таблица накопления, которая
          на живом окне растёт с нуля до сотни строк за первую минуту. Здесь у
          него сосед ровно один, и `items-start` не даёт ряду его растянуть:
          график остаётся первым блоком после липкой полосы и не едет по экрану
          вовсе — ровно то, чего добивалась прежняя правка «панель над сеткой».

          Ось времени при этом не страдает: на пятиминутке это триста точек, и
          на широком экране им остаётся больше четырёх пикселей на секунду.
          Ниже xl колонки схлопываются в стопку, и график получает всю ширину. */}
      {market.data && tokens.length > 0 && (
        <div className="grid items-start gap-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,420px)]">
          <TimelinePanel conditionId={conditionID} tokens={tokens} />
          <OrdersPanel conditionId={conditionID} tokens={tokens} market={market.data} />
        </div>
      )}

      {/* predict.fun (ADR-0040) — полной строкой под «Моими ордерами»: стаканы
          пары окна рядом с ценами Polymarket и ордера кошелька пробы. Только
          у окон, для которых сервис ищет пару (BTC/ETH/SOL 5m и 15m). */}
      {market.data && tokens.length > 0 && pfSlug && (
        <ErrorBoundary key={pfSlug} fallback={predictFallback}>
          <PredictPanel slug={pfSlug} tokens={tokens} />
        </ErrorBoundary>
      )}

      {/* ИИ-судья — полной строкой под ходом накопления (ADR-0038): ответы
          двух провайдеров читают рядом, и в колонку в 420 пикселей две
          карточки не помещаются. Ниже липкой полосы намеренно: кнопка задаёт
          вопрос, а не ставит ордер, и место ей не среди кнопок, тратящих
          деньги. Ключ — рынок: страница при переходе не перемонтируется, и без
          него выбранный запрос и ошибка кнопки переезжали бы с прошлого окна. */}
      {market.data && tokens.length > 0 && <AIPanel key={conditionID} conditionId={conditionID} />}

      {/* Биржи актива по фиду crypto-console (ADR-0039) — полной строкой под
          ИИ-судьёй: судья рассуждает по той же сводке, и его доводы проверяют,
          опустив глаза на числа. Только для BTC: трейдер берёт из фида один
          актив (CC_FEED_ASSETS=BTC), и у остальных панель всегда показывала
          бы «нет сводки». Актив — из slug, тем же разбором, что и шапка серии. */}
      {market.data && parseSeriesSlug(market.data.slug)?.asset === "BTC" && (
        <CCFeedPanel asset="BTC" />
      )}

      <div className="grid gap-2 lg:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,420px)]">
        {books &&
          tokens.map((token) => (
            <BookLadder key={token.asset_id} token={token} onPickPrice={pickPrice} />
          ))}
        {books && tokens.length === 0 && (
          <Panel title="Стакан">
            <Empty>У рынка нет outcome-токенов в справочнике — стакан показать не из чего.</Empty>
          </Panel>
        )}
        {/* Лента сделок. Сверка индекса стояла над ней и уехала наверх, в
            карточку рынка: она про базовый актив, а не про стакан, и её место —
            рядом со страйком и отсчётом, которые она и объясняет. */}
        <div className="lg:col-span-2 xl:col-span-1">
          <TapePanel conditionId={conditionID} />
        </div>

        {/* Накопление участников. Со свёрнутыми стаканами оно занимает ИХ место —
            верхние две колонки рядом с лентой: лента отвечает «что происходит
            сейчас», эта таблица — «кто за этим стоит», и читают их вместе.
            order-first, а не другой порядок в разметке: сетка одна, и переносить
            узел между ветками значило бы пересобирать панель при каждом
            переключении.

            Со стаканами — во всю ширину под ними, как было. Не в правую колонку,
            потому что колонок у неё семь, а в 420 пикселях они рвались бы
            посреди чисел — та же беда, из-за которой счётчик ленты уехал из
            шапки панели в её тело. */}
        <div
          className={
            books
              ? "lg:col-span-2 xl:col-span-3"
              : "order-first lg:col-span-2 xl:col-span-2"
          }
        >
          <ParticipantsPanel
            conditionId={conditionID}
            tokens={tokens}
            slug={market.data?.slug}
          />
        </div>


      </div>
    </div>
  );
}

/**
 * Замена упавшей панели predict.fun: проба — второстепенный раздел, и её сбой
 * не вправе ронять страницу рынка вместе с торговлей Polymarket.
 */
function predictFallback(error: Error) {
  return (
    <p className="rounded border border-[var(--color-border)] px-3 py-2 text-xs text-amber-300">
      панель predict.fun упала: {error.message} — остальная страница работает
    </p>
  );
}
